"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Plus, Trash2, Shirt } from "lucide-react";

export interface OrderItem {
  id: string;
  garmentType: string;
  qty: number;
  notes?: string;
}

interface OrderItemsEditorProps {
  items: OrderItem[];
  onChange: (items: OrderItem[]) => void;
  orderType: "ALTERATION" | "STITCHING";
}

const GARMENT_TYPES = [
  "Blouse",
  "Dress",
  "Gown",
  "Jacket",
  "Jeans",
  "Kurta",
  "Lehenga",
  "Pants",
  "Saree",
  "Shirt",
  "Shorts",
  "Skirt",
  "Suit",
  "Top",
  "Trousers",
  "Other",
];

export function OrderItemsEditor({
  items,
  onChange,
  orderType,
}: OrderItemsEditorProps) {
  function addItem() {
    const newItem: OrderItem = {
      id: `temp-${Date.now()}`,
      garmentType: "",
      qty: 1,
      notes: "",
    };
    onChange([...items, newItem]);
  }

  function updateItem(id: string, updates: Partial<OrderItem>) {
    onChange(
      items.map((item) => (item.id === id ? { ...item, ...updates } : item))
    );
  }

  function removeItem(id: string) {
    if (items.length === 1) return;
    onChange(items.filter((item) => item.id !== id));
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-medium text-slate-900">Garment Items</h3>
          <p className="text-sm text-muted-foreground">
            {orderType === "ALTERATION"
              ? "Add items you want to get altered"
              : "Add items you want to get stitched"}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={addItem}>
          <Plus className="h-4 w-4 mr-2" />
          Add Item
        </Button>
      </div>

      <div className="space-y-3">
        {items.map((item, index) => (
          <Card key={item.id}>
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <Shirt className="h-5 w-5 text-primary" />
                </div>

                <div className="flex-1 space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-muted-foreground">
                      Item {index + 1}
                    </span>
                    {items.length > 1 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removeItem(item.id)}
                        className="text-destructive hover:text-destructive"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="space-y-2">
                      <Label htmlFor={`garment-${item.id}`}>Garment Type</Label>
                      <Select
                        value={item.garmentType}
                        onValueChange={(value) =>
                          updateItem(item.id, { garmentType: value })
                        }
                      >
                        <SelectTrigger id={`garment-${item.id}`}>
                          <SelectValue placeholder="Select garment type" />
                        </SelectTrigger>
                        <SelectContent>
                          {GARMENT_TYPES.map((type) => (
                            <SelectItem key={type} value={type}>
                              {type}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor={`qty-${item.id}`}>Quantity</Label>
                      <Input
                        id={`qty-${item.id}`}
                        type="number"
                        min={1}
                        max={10}
                        value={item.qty}
                        onChange={(e) =>
                          updateItem(item.id, {
                            qty: Math.max(1, parseInt(e.target.value) || 1),
                          })
                        }
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor={`notes-${item.id}`}>
                      Special Instructions (optional)
                    </Label>
                    <Textarea
                      id={`notes-${item.id}`}
                      placeholder={
                        orderType === "ALTERATION"
                          ? "E.g., shorten hem by 2 inches, take in waist..."
                          : "E.g., preferred fabric, design reference..."
                      }
                      value={item.notes || ""}
                      onChange={(e) =>
                        updateItem(item.id, { notes: e.target.value })
                      }
                      rows={2}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {items.length === 0 && (
        <Card>
          <CardContent className="py-8 text-center">
            <Shirt className="h-10 w-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              No items added yet. Click "Add Item" to start.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
